// ─── WindRoseChart ────────────────────────────────────────────────────────────
// Hand-rolled polar chart used by the Wind Rose widget. Renders 16 compass
// sectors as wedge stacks — each sector holds up to 7 concentric speed-band
// segments, with radial length proportional to the number of paired
// observations in that (direction, band) bucket.
//
// Why hand-rolled instead of a chart lib?
//   * recharts has no polar-stack primitive
//   * react-windrose-chart on npm is published with no `dist/` folder
//     (broken), and ships React 18 as a hard dep — would break under our
//     React 19
//   * Highcharts requires a commercial license for some use cases
// The math is ~120 lines and avoids any third-party risk.
//
// Props:
//   sectors      — `[{ direction, deg, bins: [c0..c6] }]` (length 16)
//                  matches the response from /api/data/wind-rose/
//   bandColors   — `[hex × 7]` aligned with the band ordering
//                  (band 0 = <0.5 m/s, band 6 = >10 m/s)
//   bandLabels   — `[string × 7]`, used for the legend
//   size         — pixel width/height of the SVG (default 320)
//   fromDate     — ISO date string for the start of the data period
//   toDate       — ISO date string for the end of the data period
// ──────────────────────────────────────────────────────────────────────────────

import { useState } from 'react'

const COMPASS_LABELS = ['N', 'E', 'S', 'W']
const RADIAL_RINGS = 4   // number of concentric reference rings drawn

function polar(cx, cy, radius, angleRad) {
    return [cx + radius * Math.sin(angleRad), cy - radius * Math.cos(angleRad)]
}

// Build the SVG path for a single ring segment (wedge between two radii and
// two angles). Uses the standard SVG arc parameterisation.
function wedgePath(cx, cy, rInner, rOuter, aStart, aEnd) {
    const [x1, y1] = polar(cx, cy, rOuter, aStart)
    const [x2, y2] = polar(cx, cy, rOuter, aEnd)
    const [x3, y3] = polar(cx, cy, rInner, aEnd)
    const [x4, y4] = polar(cx, cy, rInner, aStart)
    const largeArc = aEnd - aStart > Math.PI ? 1 : 0
    if (rInner <= 0.01) {
        // Inner radius collapses → close the wedge through the centre
        return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2} L ${cx} ${cy} Z`
    }
    return (
        `M ${x1} ${y1} ` +
        `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2} ` +
        `L ${x3} ${y3} ` +
        `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4} Z`
    )
}

function formatDateStr(iso) {
    if (!iso) return ''
    try {
        const d = new Date(iso)
        if (isNaN(d.getTime())) return iso
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
        return iso
    }
}

export default function WindRoseChart({
    sectors = [],
    bandColors = [],
    bandLabels = [],
    size = 320,
    fromDate,
    toDate,
}) {
    const [tooltip, setTooltip] = useState(null) // { x, y, direction, bandLabel, count }

    // Defensive: bail to an empty-state if the input shape is wrong
    if (!Array.isArray(sectors) || sectors.length === 0) {
        return (
            <div
                className="flex items-center justify-center text-xs text-gray-400"
                style={{ width: size, height: size }}
            >
                Waiting for data…
            </div>
        )
    }

    // Largest sector total drives the outer radius
    const sectorTotals = sectors.map((s) => (s.bins || []).reduce((a, b) => a + b, 0))
    const maxTotal = Math.max(1, ...sectorTotals)

    const cx = size / 2
    const cy = size / 2
    const margin = 28
    const outerR = (size / 2) - margin
    const sectorCount = sectors.length             // expected = 16
    const sectorAngle = (2 * Math.PI) / sectorCount  // 22.5°

    // Reference grid: concentric rings + cross-hair compass spokes
    const ringStep = outerR / RADIAL_RINGS
    const rings = Array.from({ length: RADIAL_RINGS }, (_, i) => (i + 1) * ringStep)
    const spokeAngles = COMPASS_LABELS.map((_, i) => i * (Math.PI / 2))

    const fromStr = formatDateStr(fromDate)
    const toStr   = formatDateStr(toDate)
    const dateLabel = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr || ''

    // Tooltip x-flip: if cursor is in the right half, show tooltip to the left
    const tooltipLeft = tooltip
        ? (tooltip.x > size * 0.55 ? tooltip.x - 148 : tooltip.x + 14)
        : 0
    const tooltipTop = tooltip ? Math.max(4, tooltip.y - 44) : 0

    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                role="img"
                aria-label="Wind rose"
                onMouseLeave={() => setTooltip(null)}
            >
                {/* Reference rings */}
                {rings.map((r, i) => (
                    <circle
                        key={`ring-${i}`}
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth="1"
                        strokeDasharray={i === RADIAL_RINGS - 1 ? '0' : '2,3'}
                    />
                ))}

                {/* Compass spokes (N/E/S/W) */}
                {spokeAngles.map((a, i) => {
                    const [x, y] = polar(cx, cy, outerR, a)
                    return (
                        <line
                            key={`spoke-${i}`}
                            x1={cx}
                            y1={cy}
                            x2={x}
                            y2={y}
                            stroke="#E5E7EB"
                            strokeWidth="1"
                        />
                    )
                })}

                {/* Stacked wedges per sector × band */}
                {sectors.map((sec, secIdx) => {
                    const total = sectorTotals[secIdx]
                    if (total === 0) return null
                    const aCentre = secIdx * sectorAngle
                    const aStart = aCentre - sectorAngle / 2 + 0.02 // small gap between sectors
                    const aEnd   = aCentre + sectorAngle / 2 - 0.02
                    let rInner = 0
                    return sec.bins.map((count, bandIdx) => {
                        if (count <= 0) return null
                        const fraction = count / maxTotal
                        const rOuter = rInner + fraction * outerR
                        const path = wedgePath(cx, cy, rInner, rOuter, aStart, aEnd)
                        const node = (
                            <path
                                key={`s${secIdx}-b${bandIdx}`}
                                d={path}
                                fill={bandColors[bandIdx] || '#999'}
                                stroke="#fff"
                                strokeWidth="0.5"
                                style={{ cursor: 'crosshair' }}
                                onMouseMove={(e) => {
                                    const rect = e.currentTarget.closest('svg').getBoundingClientRect()
                                    setTooltip({
                                        x: e.clientX - rect.left,
                                        y: e.clientY - rect.top,
                                        direction: sec.direction,
                                        bandLabel: bandLabels[bandIdx] || '',
                                        count,
                                    })
                                }}
                                onMouseLeave={() => setTooltip(null)}
                            />
                        )
                        rInner = rOuter
                        return node
                    })
                })}

                {/* Compass cardinal labels — drawn last so they sit on top */}
                {COMPASS_LABELS.map((label, i) => {
                    const a = i * (Math.PI / 2)
                    const [x, y] = polar(cx, cy, outerR + 14, a)
                    return (
                        <text
                            key={`lbl-${label}`}
                            x={x}
                            y={y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="11"
                            fontWeight="600"
                            fill="#4B5563"
                        >
                            {label}
                        </text>
                    )
                })}
            </svg>

            {/* Hover tooltip */}
            {tooltip && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipLeft,
                        top: tooltipTop,
                        pointerEvents: 'none',
                        zIndex: 20,
                        minWidth: 130,
                    }}
                    className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs text-gray-700"
                >
                    <div className="font-semibold text-gray-900">
                        {tooltip.direction} &middot; {tooltip.bandLabel}
                    </div>
                    <div className="text-gray-500 mt-0.5">
                        {tooltip.count} observation{tooltip.count !== 1 ? 's' : ''}
                    </div>
                    {dateLabel && (
                        <div className="text-gray-400 mt-1 border-t border-gray-100 pt-1">
                            {dateLabel}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
